import { useSelector } from 'react-redux';
import { Vector as VectorSource } from 'ol/source.js';
import { useEffect, useMemo } from 'react';
import { selectFilteredEdges } from '../map/slice/mapSelectors';
import GeoJSON from 'ol/format/GeoJSON.js';
import { WebGLLayer } from './webglLayer';
import { fromLonLat } from 'ol/proj';
import { selectContracts } from '../contracts/contractsSlice';
import { Edge, PartialRecord } from '@/types';
import { FeatureLike } from '../utils/geoJsonUtils';
import { RootState } from '@/store';
import { colors } from '../utils/colors';
import { useMapContext } from './context/MapContext';

const PRIORITY_CLASSES = ['IN_PATH', 'OUT_OF_PATH', 'IT'];
const PRIORITY_COLORS: Record<number, string> = {
  1: colors.elements.contractPath.IN_PATH,
  2: colors.elements.contractPath.OUT_OF_PATH,
  3: colors.elements.contractPath.IT,
};
const PRIORITY_LEVELS: PartialRecord<'IN_PATH' | 'OUT_OF_PATH' | 'IT', number> =
  PRIORITY_CLASSES.reduce(
    (result, key, index) => ({
      ...result,
      [key as 'IN_PATH' | 'OUT_OF_PATH' | 'IT']:
        PRIORITY_CLASSES.length - index,
    }),
    {},
  );

const getContractStyle = () => {
  return {
    'stroke-width': 10,
    'stroke-color': ['get', 'color'],
    'stroke-line-cap': 'round',
    'stroke-line-join': 'round',
  };
};

type EdgeContracts = {
  edgeId: number;
  IN_PATH?: { contracts: number[]; total: number };
  IT?: { contracts: number[]; total: number };
  OUT_OF_PATH?: { contracts: number[]; total: number };
  color?: string;
  firm_total: number;
  highest_priority_number?: number;
};

const contractToGeoJson = (
  edgeContracts: EdgeContracts,
  edges: Record<string, Edge>,
): FeatureLike => {
  const edge = edges[edgeContracts.edgeId];

  return {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: edge.positions.map(([lat, lon]) => fromLonLat([lon, lat])),
    },
    properties: {
      id: edge.id,
      key: `contract-${edge.id}`,
      color: edgeContracts.color,
    },
  };
};

export const MapContracts = ({ zIndex = 0 }) => {
  const { map } = useMapContext();
  const contracts = useSelector(selectContracts);
  const edges = useSelector(selectFilteredEdges);
  const showContracts = useSelector(
    (state: RootState) => state.plotter.showContracts,
  );

  const edgeContracts = useMemo<Record<string, EdgeContracts>>(() => {
    if (!showContracts || !contracts || !edges) {
      return {};
    }
    const _edgeContracts: Record<string, EdgeContracts> = {};
    Object.values(contracts)
      .filter(Boolean)
      .forEach((contract) => {
        contract.edge_classification?.forEach((edge_class) => {
          edge_class.edge_ids.forEach((edgeId) => {
            if (!_edgeContracts[edgeId.toString()]) {
              _edgeContracts[edgeId.toString()] = PRIORITY_CLASSES.reduce(
                (object, priorityClass) => ({
                  ...object,
                  [priorityClass]: { contracts: [], total: 0 },
                }),
                {
                  edgeId: edgeId,
                  firm_total: 0,
                },
              );
            }

            const highest_priority_number = Math.max(
              _edgeContracts[edgeId].highest_priority_number || 0,
              PRIORITY_LEVELS[edge_class.type] ?? 0,
            );
            _edgeContracts[edgeId].highest_priority_number =
              highest_priority_number;
            _edgeContracts[edgeId].color =
              PRIORITY_COLORS[highest_priority_number];
            _edgeContracts[edgeId].firm_total += Number(
              edge_class.type != 'IT' ? contract.max_daily_quantity : 0,
            );
            _edgeContracts[edgeId]![edge_class.type]!.total += Number(
              contract.max_daily_quantity,
            );
            _edgeContracts[edgeId]![edge_class.type]!.contracts.push(
              Number(contract.id),
            );
          });
        });
      });
    return _edgeContracts;
  }, [contracts, edges, showContracts]);

  const contractsLayer = useMemo(() => {
    const source = new VectorSource({
      features: new GeoJSON().readFeatures({
        type: 'FeatureCollection',
        features: Object.values(edgeContracts ?? {})
          .filter((edgeContract) => Boolean(edges[edgeContract.edgeId]))
          .map((edgeContract) => contractToGeoJson(edgeContract, edges)),
      }),
      format: new GeoJSON(),
    });

    return new WebGLLayer({
      source,
      style: getContractStyle(),
      zIndex,
    });
  }, [edges, contracts, edgeContracts]);

  useEffect(() => {
    if (!map || !contractsLayer) return;
    map.addLayer(contractsLayer);
    return () => {
      contractsLayer.dispose();
      if (map.getAllLayers().includes(contractsLayer)) {
        map.removeLayer(contractsLayer);
      }
    };
  }, [map, contractsLayer]);

  return null;
};
